import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  TrendingUp,
  RefreshCw,
  MapPin,
  Truck as TruckIcon,
  Search,
  ArrowRight,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WINDOWS = [
  { value: 7, label: 'Last 7 days' },
  { value: 30, label: 'Last 30 days' },
  { value: 90, label: 'Last 90 days' },
];

const formatCurrency = (n) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n || 0);

const formatRelative = (iso) => {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  const diffMin = Math.max(1, Math.round((Date.now() - then) / 60000));
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.round(diffMin / 60);
  if (diffH < 48) return `${diffH}h ago`;
  return `${Math.round(diffH / 24)}d ago`;
};

export const MarketIntelTab = () => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/quote/lanes/top?days=${days}&limit=15`);
      setData(res.data);
    } catch {
      toast.error('Failed to load market intelligence');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="w-8 h-8 animate-spin text-zinc-400" />
      </div>
    );
  }

  const lanes = data?.lanes || [];
  const total = data?.total_searches || 0;
  const topSearches = lanes[0]?.searches || 0;

  return (
    <div className="space-y-6">
      {/* Header strip */}
      <div className="bg-white border border-zinc-200 p-6 flex flex-col md:flex-row gap-4 md:items-center md:justify-between">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-red-600/10 text-red-600 flex items-center justify-center flex-shrink-0">
            <TrendingUp className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-lg font-black text-zinc-900">
              Top Searched Lanes
            </h2>
            <p className="text-sm text-zinc-500">
              Anonymous quote attempts from the homepage calculator — what
              shippers are looking for, right now.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="border border-zinc-300 px-3 py-2 text-sm bg-white"
            data-testid="market-intel-window-select"
          >
            {WINDOWS.map((w) => (
              <option key={w.value} value={w.value}>
                {w.label}
              </option>
            ))}
          </select>
          <button
            onClick={fetchData}
            className="border border-zinc-300 hover:border-zinc-900 px-3 py-2 text-sm transition-colors flex items-center gap-2"
            data-testid="market-intel-refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Stat strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard
          label="Total Searches"
          value={total.toLocaleString()}
          icon={Search}
        />
        <StatCard
          label="Unique Lanes"
          value={lanes.length.toLocaleString()}
          icon={MapPin}
        />
        <StatCard
          label="Top Lane Volume"
          value={topSearches.toLocaleString()}
          icon={TrendingUp}
        />
        <StatCard
          label="Window"
          value={`${days} days`}
          icon={RefreshCw}
        />
      </div>

      {/* Table */}
      {lanes.length === 0 ? (
        <div className="text-center py-16 bg-white border border-zinc-200">
          <Search className="w-12 h-12 text-zinc-300 mx-auto mb-3" />
          <h3 className="font-bold text-zinc-700">No searches yet</h3>
          <p className="text-sm text-zinc-500 mt-1">
            Quote attempts from the homepage will appear here.
          </p>
        </div>
      ) : (
        <div className="bg-white border border-zinc-200 overflow-x-auto">
          <table className="w-full text-sm" data-testid="market-intel-table">
            <thead>
              <tr className="border-b border-zinc-200 bg-zinc-50">
                <th className="text-left px-5 py-3 text-[11px] font-bold uppercase tracking-wider text-zinc-500 w-10">
                  #
                </th>
                <th className="text-left px-5 py-3 text-[11px] font-bold uppercase tracking-wider text-zinc-500">
                  Lane
                </th>
                <th className="text-left px-5 py-3 text-[11px] font-bold uppercase tracking-wider text-zinc-500">
                  Equipment
                </th>
                <th className="text-right px-5 py-3 text-[11px] font-bold uppercase tracking-wider text-zinc-500">
                  Searches
                </th>
                <th className="text-right px-5 py-3 text-[11px] font-bold uppercase tracking-wider text-zinc-500">
                  Avg Miles
                </th>
                <th className="text-right px-5 py-3 text-[11px] font-bold uppercase tracking-wider text-zinc-500">
                  Avg Rate
                </th>
                <th className="text-right px-5 py-3 text-[11px] font-bold uppercase tracking-wider text-zinc-500">
                  Last Searched
                </th>
              </tr>
            </thead>
            <tbody>
              {lanes.map((lane, idx) => {
                const heatPct = topSearches > 0 ? (lane.searches / topSearches) * 100 : 0;
                return (
                  <tr
                    key={`${lane.origin_state}-${lane.destination_state}-${lane.equipment_type}`}
                    className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50 transition-colors"
                    data-testid={`market-intel-row-${idx}`}
                  >
                    <td className="px-5 py-4 text-zinc-400 font-mono text-xs">
                      {String(idx + 1).padStart(2, '0')}
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2 font-bold text-zinc-900">
                        <span>{lane.origin_state}</span>
                        <ArrowRight className="w-3.5 h-3.5 text-red-600" />
                        <span>{lane.destination_state}</span>
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-zinc-100 text-zinc-700 text-xs font-semibold">
                        <TruckIcon className="w-3 h-3" />
                        {lane.equipment_type}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <div className="w-20 h-1.5 bg-zinc-100 overflow-hidden">
                          <div
                            className="h-full bg-red-600"
                            style={{ width: `${heatPct}%` }}
                          />
                        </div>
                        <span className="font-bold text-zinc-900 tabular-nums w-8 text-right">
                          {lane.searches}
                        </span>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-right text-zinc-600 tabular-nums">
                      {lane.avg_miles.toLocaleString()}
                    </td>
                    <td className="px-5 py-4 text-right font-bold text-zinc-900 tabular-nums">
                      {formatCurrency(lane.avg_rate)}
                    </td>
                    <td className="px-5 py-4 text-right text-zinc-500 text-xs">
                      {formatRelative(lane.last_searched)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const StatCard = ({ label, value, icon: Icon }) => (
  <div className="bg-white border border-zinc-200 p-4">
    <div className="flex items-center gap-2 text-zinc-500 text-[10px] font-bold uppercase tracking-widest mb-2">
      <Icon className="w-3 h-3" />
      {label}
    </div>
    <div className="text-2xl font-black text-zinc-900 tabular-nums">{value}</div>
  </div>
);

export default MarketIntelTab;
